import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { NotFoundPageComponent } from './components/not-found-page/not-found-page.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { NgApexchartsModule } from 'ng-apexcharts';
import { DirectivesModule } from '../common-modules/directives/directives.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FeatureFlagsService } from './services/feature-flags.service';
import { AuthModule as CzAuthModule } from './modules/auth/auth.module';
import { AddCsrfTokenInterceptor } from './modules/auth/interceptors/add-csrf-token-interceptor.service';
import { FullPageComponent } from './components/full-page/full-page.component';
import { LoggingService } from './services/logging/logging.service';
import { CustomerQuestionnaireModule } from './modules/customer-questionnaire/customer-questionnaire.module';
import { ConfigService } from './services/config/config.service';
import { AppConfig } from './services/config/types/app-config.type';
import { firstValueFrom } from 'rxjs';
import { Router, RouteReuseStrategy } from '@angular/router';
import { SimpleRouteReuseStrategy } from './route-cache-strategies/simple-route-reuse.strategy';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './modules/auth/services/auth.service';
import { SsoErrorComponent } from './components/sso-error/sso-error.component';
import { CompaniesService } from './modules/companies/services/companies.service';
import { IdentityService } from './modules/auth/services/identity.service';
import { MdrInterceptor } from './modules/auth/interceptors/mdr.interceptor';
import { UnauthorizedInterceptor } from './modules/auth/interceptors/unauthorized.interceptor';
import { AppSwitcherComponent } from './components/app-switcher/app-switcher.component';

// import { RECAPTCHA_V3_SITE_KEY, RecaptchaFormsModule, RecaptchaV3Module } from 'ng-recaptcha';

@NgModule({
    declarations: [
        AppComponent,
        DashboardComponent,
        FullPageComponent,
        SidenavComponent,
        NotFoundPageComponent,
        SsoErrorComponent,
        AppSwitcherComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserAnimationsModule,
        CommonModule,
        DirectivesModule,
        DragDropModule,
        FormsModule,
        MatAutocompleteModule,
        MatBadgeModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCardModule,
        MatCheckboxModule,
        MatChipsModule,
        MatDatepickerModule,
        MatDialogModule,
        MatDividerModule,
        MatExpansionModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        MatMenuModule,
        MatNativeDateModule,
        MatPaginatorModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRadioModule,
        MatSelectModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatSnackBarModule,
        MatSortModule,
        MatStepperModule,
        MatTableModule,
        MatTabsModule,
        MatToolbarModule,
        MatTooltipModule,
        NgApexchartsModule,
        MatCardModule,
        CustomerQuestionnaireModule,
        MatCardModule,
        ReactiveFormsModule,
        CzAuthModule,
        // RecaptchaV3Module,
        // RecaptchaFormsModule,
    ],
    providers: [
        LoggingService,
        provideHttpClient(),
        provideAppInitializer(async () => {
            const httpBackend: HttpBackend = inject(HttpBackend);
            const featureFlagService: FeatureFlagsService = inject(FeatureFlagsService);
            const configService: ConfigService = inject(ConfigService);
            const authService: AuthService = inject(AuthService);
            const companiesService: CompaniesService = inject(CompaniesService);
            const identityService: IdentityService = inject(IdentityService);

            const loadedConfig = await firstValueFrom(new HttpClient(httpBackend).get<AppConfig>('/assets/appConfig.json'));
            const url = new URL(window.location.href);
            const idpUrl = url.searchParams.get('idpUrl');

            if (!localStorage.getItem('reff')) {
                localStorage.setItem('reff', document.referrer);
            }

            featureFlagService.set(loadedConfig.features);
            configService.set(loadedConfig);

            if (idpUrl !== null) {
                authService.initiateSsoLogin({ metadataUrl: idpUrl });

                // rejecting app_initializer prevents the app from loading further
                return new Promise((_, reject) => {
                    console.log('Redirecting to IDP for auto-login');
                    reject();
                });
            }

            if (authService.hasRefreshToken() && authService.shouldRefreshJwtTokens()) {
                await firstValueFrom(authService.refreshTokens());
            }

            if (authService.jwtUser !== null) {
                const userCompany = await firstValueFrom(companiesService.getCompanyDetails(authService.jwtUser.CompanyId));
                await identityService.setCompany(userCompany);
            }

            return;
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (authService: AuthService) => {
                return new UnauthorizedInterceptor(authService);
            },
            multi: true,
            deps: [AuthService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (cookieService: CookieService, authService: AuthService) => {
                return new AddCsrfTokenInterceptor(cookieService, authService);
            },
            multi: true,
            deps: [CookieService, AuthService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (authService: AuthService, configService: ConfigService, router: Router) => {
                return new MdrInterceptor(authService, configService, router);
            },
            multi: true,
            deps: [AuthService, ConfigService, Router],
        },
        {
            provide: RouteReuseStrategy,
            useClass: SimpleRouteReuseStrategy,
        },
        CookieService,
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimationsAsync(),
    ],
})
export class AppModule {}
